import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const VIEWPORT_PAD = 8;

const menuEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
`;

const Panel = styled.div`
  position: fixed;
  z-index: 9999;
  min-width: 140px;
  max-width: 220px;
  padding: 4px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 24px 48px rgba(0, 0, 0, 0.16);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--text-secondary);
  transform-origin: top left;
  animation: ${menuEnter} 0.14s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const DividerLine = styled.div`
  height: 1px;
  background: var(--border-subtle);
  margin: 3px 6px 4px;
`;

const ItemBtn = styled.button<{ $disabled?: boolean; $color?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 9px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font: inherit;
  font-weight: 500;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  color: ${({ $disabled, $color }) =>
    $disabled ? 'var(--text-tertiary)' : ($color ?? 'var(--text-secondary)')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  transition: background-color 0.08s ease, color 0.08s ease;

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    color: ${({ $color }) => ($color ?? 'var(--text-primary)')};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
  }
`;

const ItemIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  color: currentColor;

  & > svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const ItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: normal;
  hyphens: auto;
`;

export type ContextMenuItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  color?: string;
  divider?: boolean;
  disabled?: boolean;
};

export type ContextMenuProps = {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
};

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  const clampToViewport = useCallback(() => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + rect.width > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - rect.width - VIEWPORT_PAD);
    }
    if (top + rect.height > vh - VIEWPORT_PAD) {
      top = Math.max(VIEWPORT_PAD, vh - rect.height - VIEWPORT_PAD);
    }
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - rect.width - VIEWPORT_PAD));
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - rect.height - VIEWPORT_PAD));
    setPos({ left, top });
  }, [x, y]);

  useLayoutEffect(() => {
    setPos({ left: x, top: y });
  }, [x, y]);

  useLayoutEffect(() => {
    clampToViewport();
  }, [clampToViewport, items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const onResize = () => clampToViewport();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampToViewport]);

  const handleBackdropContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
  };

  const node = (
    <>
      <Backdrop
        role="presentation"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
        onContextMenu={handleBackdropContextMenu}
      />
      <Panel ref={panelRef} role="menu" style={{ left: pos.left, top: pos.top }}>
        {items.map((item) => (
          <React.Fragment key={item.id}>
            {item.divider ? <DividerLine aria-hidden /> : null}
            <ItemBtn
              type="button"
              role="menuitem"
              disabled={item.disabled}
              $disabled={item.disabled}
              $color={item.color}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                onClose();
              }}
            >
              {item.icon != null ? <ItemIcon>{item.icon}</ItemIcon> : null}
              <ItemLabel>{item.label}</ItemLabel>
            </ItemBtn>
          </React.Fragment>
        ))}
      </Panel>
    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};
